import { getMappingsForGroup } from "@/configuration/document-mappings";
import type { DocumentMappingRegistry } from "@/configuration/document-mappings/types";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import { PINNACLE_PRACTITIONER_MAPPINGS } from "@/configuration/groups/pinnacle/generated/practitioner-mappings";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { getTransformConfig } from "@/configuration/transform-config";
import type { FieldTransformConfig, LabAbbreviation } from "@/configuration/transform-config";
export type { FieldTransformConfig, LabAbbreviation } from "@/configuration/transform-config";
import { logger } from "@/src/lib/logging/logger";
import { formatDateWithDots, formatDateWithSpec } from "@/src/lib/utils/date-and-phone-utils";
import { PriorAuthorizationStatus } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Group } from "@/src/models/group";
import { getPersonName } from "@/src/models/person";
import { Practitioner, getProviderNameWithCredentials } from "@/src/models/practitioner";

// Options for value processing
interface ProcessValueOptions {
  /** Whether to treat string values as potential dates */
  parseDates?: boolean;
}

// Default processing options
const DEFAULT_OPTIONS: ProcessValueOptions = {
  parseDates: false,
};

/**
 * Attempts to parse a string as a date and format it
 */
function tryParseAndFormatDate(value: string): string | null {
  const date = new Date(value);

  return !isNaN(date.getTime()) ? formatDateWithDots(date) : null;
}

/**
 * Extracts a display value from an object using common property names
 */
function extractObjectDisplayValue(obj: Record<string, unknown>): string {
  const displayProperties = ["displayName", "name", "label", "title", "text", "value"];
  const foundProp = displayProperties.find((prop) => prop in obj && obj[prop] != null);

  return foundProp ? String(obj[foundProp]) : "";
}

/**
 * Handles array values by recursively processing each item
 */
function processArrayValue(value: unknown[], processor: (val: unknown) => string): string {
  return value.map((item) => processor(item)).join(", ");
}

/**
 * Main value processing function that handles all types consistently
 */
export function processValue(value: unknown, options: ProcessValueOptions = DEFAULT_OPTIONS): string {
  // Handle null/undefined
  if (value == null) return "";

  // Handle arrays
  if (Array.isArray(value)) {
    return processArrayValue(value, (item) => processValue(item, options));
  }

  // Handle Date objects
  if (value instanceof Date) {
    return formatDateWithDots(value);
  }

  // Handle objects (but not Date objects)
  if (typeof value === "object" && value !== null) {
    const displayValue = extractObjectDisplayValue(value as Record<string, unknown>);

    if (displayValue) {
      return displayValue;
    }

    // Handle single-property objects
    const keys = Object.keys(value);

    if (keys.length === 1) {
      return processValue((value as Record<string, unknown>)[keys[0]], options);
    }

    // Fallback to JSON string
    return JSON.stringify(value);
  }

  // Handle string dates
  if (typeof value === "string" && options.parseDates) {
    const formattedDate = tryParseAndFormatDate(value);

    if (formattedDate) {
      return formattedDate;
    }
  }

  // Handle all other types
  return String(value);
}

function abbreviateLabName(name: string): string {
  const words = name.trim().split(/\s+/);

  if (words.length < 2) return name.trim();

  return words.map((w) => w[0].toUpperCase()).join("");
}

function normalizeLabName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function applyCanonicalAbbreviations(
  list: string[],
  abbreviations: readonly LabAbbreviation[],
  priorityAbbreviations: readonly string[],
  maxRange: number,
  maxLength: number
): string {
  const aliasLookup = new Map<string, string>();

  for (const entry of abbreviations) {
    for (const alias of entry.aliases) {
      aliasLookup.set(normalizeLabName(alias), entry.canonical);
    }
  }

  const mapped = list.map((name) => aliasLookup.get(normalizeLabName(name)) ?? name);

  const priorityItems: string[] = [];

  for (const priority of priorityAbbreviations) {
    for (const item of mapped) {
      if (item === priority) priorityItems.push(item);
    }
  }

  const prioritySet = new Set<string>(priorityAbbreviations);
  const remainingItems = mapped.filter((item) => !prioritySet.has(item));

  const selected = [...priorityItems, ...remainingItems].slice(0, maxRange);

  let joined = selected.join(", ");

  while (joined.length > maxLength && selected.length > 0) {
    selected.pop();
    joined = selected.join(", ");
  }

  return joined;
}

function isLikelyDateFieldName(fieldName: string): boolean {
  return fieldName.toLowerCase().includes("date");
}

/**
 * Checks if a field name or value is date-related
 */
function isLikelyDateField(fieldName: string, value: unknown): boolean {
  return (
    isLikelyDateFieldName(fieldName) ||
    value instanceof Date ||
    (typeof value === "string" && !isNaN(Date.parse(value)))
  );
}

/**
 * Applies custom transformations based on field name and configuration
 */
function applyCustomTransformations(
  key: string,
  value: unknown,
  config: FieldTransformConfig,
  data: Record<string, unknown>,
  formatSpec?: string
): string | null {
  const resolveProvider = () =>
    (data.provider ?? data.orderingProvider ?? data.receivingProvider) as Practitioner | null | undefined;

  const transformers = {
    signatureRequired: (val: unknown) => {
      if (!config.signatureRequired) return "";

      return val ? config.signatureRequired.trueValue : config.signatureRequired.falseValue;
    },
    vaccineType: (val: unknown) => {
      if (!config.vaccineType) return null;

      if (val === "" || val == null) return config.vaccineType.emptyValue;

      return processValue(val, { parseDates: false }) + config.vaccineType.suffix;
    },
    supplyDuration: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    clinicName: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    companyName: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    bodyPart: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    providerLastName: (val: unknown) => (data.orderingProvider as Practitioner)?.familyName || String(val),
    provider: (val: unknown) => {
      const p = resolveProvider();

      return p != null ? getProviderNameWithCredentials(p) : String(val);
    },
    providerName: (val: unknown) => {
      const p = resolveProvider();

      if (p != null) return getPersonName(p, false);
      if (val === null || val === undefined) return null;

      return String(val);
    },
    location: (val: unknown) => {
      if (val == null) return "";
      if (typeof val === "object" && "name" in val) return (val as { name: string | null }).name ?? "";

      return String(val);
    },
    referringProvider: (val: unknown) => (val ? getProviderNameWithCredentials(val as Practitioner) : String(val)),
    isSigned: (val: unknown) => {
      if (!config.isSigned) return null;

      return val ? config.isSigned.trueValue : config.isSigned.falseValue;
    },
    significantFindings: (val: unknown) => {
      if (!config.significantFindings) return "";

      return val ? config.significantFindings.trueValue : config.significantFindings.falseValue;
    },
    abnormal: (val: unknown) => {
      if (!config.abnormal) return "";

      return val ? config.abnormal.trueValue : config.abnormal.falseValue;
    },
    followUpRequired: (val: unknown) => {
      if (!config.followUpRequired) return "";

      return val ? config.followUpRequired.trueValue : config.followUpRequired.falseValue;
    },
    bodyPartLaterality: (val: unknown) => {
      if (val === null || val === undefined || val === "") return "";

      const canonical = String(val)
        .toLowerCase()
        .replace(/[\s._-]/g, "");

      if (canonical === "other") return "";

      return `${String(val)} `;
    },
    // Falls back to the legacy scalar `bodyPart` when `bodyParts` isn't populated — extraction and
    // sync run as separate cloud tasks, so a referral extracted before a `bodyParts` rollout (but not
    // yet synced) would otherwise render this var blank instead of showing its already-extracted value.
    bodyParts: (val: unknown) => {
      if (Array.isArray(val)) return val.join(", ");
      if (typeof data.bodyPart === "string" && data.bodyPart.length > 0) return data.bodyPart;

      return "";
    },
    orderNumber: (val: unknown) => {
      if (!config.orderNumber) return "";

      if (val === null || val === undefined) return config.orderNumber.emptyValue;

      return String(val);
    },
    specialty: (val: unknown) => {
      if (!config.specialty) return "";

      if (val === null || val === undefined) return config.specialty.emptyValue;

      return String(val);
    },
    authorizationStatus: (val: unknown) => {
      // Opt-in via "{{ authorizationStatus deniedOnly }}" — plain "{{ authorizationStatus }}" (used by
      // TCO's other prior-auth response labels/tasks) must keep showing the raw status unconditionally.
      if (formatSpec !== "deniedOnly") return null;

      return val === PriorAuthorizationStatus.DENIED ? "DENIED" : "";
    },
    listOfNameOfLabs: (val: unknown) => {
      if (!config.listOfNameOfLabs) return null;

      if (val === null || val === undefined) return null;

      const list = (Array.isArray(val) ? val : [val]).map(String);
      const { maxLength, maxRange, abbreviations, priorityAbbreviations } = config.listOfNameOfLabs;

      if (abbreviations && priorityAbbreviations) {
        return applyCanonicalAbbreviations(list, abbreviations, priorityAbbreviations, maxRange, maxLength);
      }

      const joined = list.join(", ");

      if (joined.length <= maxLength) return joined;

      const abbreviated = list.map(abbreviateLabName);
      const abbreviatedJoined = abbreviated.join(", ");

      if (abbreviatedJoined.length <= maxLength) return abbreviatedJoined;

      return abbreviated
        .toSorted((a, b) => a.length - b.length)
        .slice(0, maxRange)
        .join(", ");
    },
    usStateName: (val: unknown) => {
      if (val === "" || val == null) return "";

      return processValue(val, { parseDates: false });
    },
    labTypesAndResults: (val: unknown) => {
      if (!config.labTypesAndResults) return null;

      if (val === null || val === undefined) return null;

      const list = Array.isArray(val) ? val : [val];

      // Extract typeOfLab from each object and filter out null/undefined values
      const typeOfLabs = list
        .map((item) => {
          if (typeof item === "object" && item !== null && "typeOfLab" in item) {
            return item.typeOfLab;
          }

          return null;
        })
        .filter((type): type is string => type !== null && type !== undefined);

      if (typeOfLabs.length === 0) return null;

      if (typeOfLabs.length > config.labTypesAndResults.maxRange) {
        return typeOfLabs.slice(0, config.labTypesAndResults.maxRange).join(", ");
      }

      return typeOfLabs.join(", ");
    },
  };

  const transformer = transformers[key as keyof typeof transformers];

  return transformer ? transformer(value) : null;
}

/**
 * Renders a template string by replacing placeholders with values from data
 */
export function renderTemplate(
  template: string | null | undefined,
  data: Record<string, unknown>,
  group: Pick<Group, "subdomain">,
  options?: { config?: FieldTransformConfig; stripUnresolved?: boolean }
): string {
  if (!template) {
    return "";
  }

  const effectiveConfig = options?.config ?? getTransformConfig(group);

  const rendered = template
    .replace(/\{\{([^}]+)\}\}/g, (match, key) => {
      const [fieldName, formatSpec] = key.trim().split(/\s+/);
      const value = data[fieldName];

      const customResult = applyCustomTransformations(fieldName, value, effectiveConfig, data, formatSpec);

      if (customResult !== null) return customResult;

      if (value === undefined || value === null) {
        return match;
      }

      if (formatSpec && isLikelyDateField(fieldName, value)) {
        return formatDateWithSpec(value, formatSpec);
      }

      return processValue(value, { parseDates: isLikelyDateFieldName(fieldName) });
    })
    .trim();

  if (options?.stripUnresolved) {
    return rendered
      .replace(/\{\{[^}]*\}\}/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  return rendered;
}

// Filename policy for hasOwnMappings / mapping-derived filenames — not a mapping registry.
// Nimbus is omitted on purpose to keep legacy date-based filenames.
const GROUPS_WITH_MAPPINGS_SUBDOMAINS = new Set<string>([
  ConfigSubdomain.APEX,
  ConfigSubdomain.AUTONOMY,
  ConfigSubdomain.ENT_PARTNERS,
  ConfigSubdomain.MASS_ENT,
  ConfigSubdomain.PINNACLE,
  ConfigSubdomain.HEARTPLACE,
  ConfigSubdomain.VALERIE,
  ConfigSubdomain.VALERIE_HEALTH_TRAINING,
  ConfigSubdomain.COUG,
  ConfigSubdomain.BALANCE_HEALTH,
  ConfigSubdomain.STRIDECARE,
  ConfigSubdomain.USHV_CCOH,
  ConfigSubdomain.USHV_FORTWORTH,
  ConfigSubdomain.USHV_MCA,
  ConfigSubdomain.USHV_MKC,
  ConfigSubdomain.USHV_ORION,
  ConfigSubdomain.TEXAS_HEART_SOUTH_HEART,
  ConfigSubdomain.TRICITY,
  ConfigSubdomain.VPS,
  ConfigSubdomain.PA_CENTRAL_REGION,
  ConfigSubdomain.PA_EAST_REGION,
  ConfigSubdomain.SIENT,
  ConfigSubdomain.PACESETTER,
  ConfigSubdomain.PPCP,
]);

export function hasOwnMappings(group: Group): boolean {
  return GROUPS_WITH_MAPPINGS_SUBDOMAINS.has(getGroupConfigSubdomain(group));
}

/**
 * Runtime document-mapping lookup. Delegates to `getMappingsForGroup`.
 * Unknown groups warn and return `{}` so request paths do not throw.
 */
export function getMappings(group: Group): DocumentMappingRegistry {
  const subdomain = getGroupConfigSubdomain(group);

  try {
    return getMappingsForGroup(subdomain);
  } catch (error) {
    if (!(error instanceof Error) || !error.message.startsWith("No document mappings defined")) {
      throw error;
    }

    logger.warn("No document mappings registered for group, returning empty registry", {
      subdomain,
    });

    return {};
  }
}

/**
 * Get the appropriate practitioner mappings based on group name
 * @param group - The group to get the practitioner mappings for
 * @returns The appropriate practitioner mappings based on group name
 */
export function getPractitionerMappings(group: Group) {
  switch (getGroupConfigSubdomain(group)) {
    case ConfigSubdomain.AUTONOMY:
      return AUTONOMY_PRACTITIONER_MAPPINGS;
    case ConfigSubdomain.PINNACLE:
      return PINNACLE_PRACTITIONER_MAPPINGS;
    default:
      return PINNACLE_PRACTITIONER_MAPPINGS;
  }
}
